import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-gray-50 py-20 lg:py-24" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left: Contact Info */}
          <div data-testid="contact-info">
            <div className="text-xs font-semibold uppercase tracking-[0.15em] text-[#003151] mb-4" data-testid="contact-overline">
              Get in Touch
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight font-heading text-[#1a1a1a] mb-6" data-testid="contact-heading">
              Let's Discuss Your
              <br />
              Next Project
            </h2>
            <p className="text-gray-600 leading-relaxed mb-10" data-testid="contact-description">
              Ready to start your project? Contact us today for a consultation 
              and quote. Our team is ready to help.
            </p>

            {/* Contact Methods */}
            <div className="space-y-6">
              <a
                href="tel:+91 7292909090"
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="p-3 bg-[#003151]/10 rounded group-hover:bg-[#003151] group-hover:text-white transition-colors">
                  <Phone className="w-5 h-5 text-[#003151] group-hover:text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Phone</div>
                  <div className="font-semibold text-[#1a1a1a] group-hover:text-[#003151] transition-colors">+91 7292909090</div>
                </div>
              </a>

              <a
                href="mailto:contact@aurumprintsassociates.com"
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="p-3 bg-[#003151]/10 rounded group-hover:bg-[#003151] group-hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-[#003151] group-hover:text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Email</div>
                  <div className="font-semibold text-[#1a1a1a] group-hover:text-[#003151] transition-colors">contact@aurumprintsassociates.com</div>
                </div>
              </a>

              <div className="flex items-center gap-4" data-testid="contact-address">
                <div className="p-3 bg-[#003151]/10 rounded">
                  <MapPin className="w-5 h-5 text-[#003151]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Address</div>
                  <div className="text-gray-700">
                    BIHAR OFFSET, SABZIBAGH<br />
                    PATNA, BIHAR 800004
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200 shadow-sm" data-testid="contact-form-container">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-wider text-gray-600 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#003151] focus:bg-white transition-colors"
                    //placeholder="First Name Last Name"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-wider text-gray-600 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#003151] focus:bg-white transition-colors"
                    //placeholder="name@company.com"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="phone" className="block text-xs uppercase tracking-wider text-gray-600 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#003151] focus:bg-white transition-colors"
                    //placeholder="+1 234 567 890"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-xs uppercase tracking-wider text-gray-600 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#003151] focus:bg-white transition-colors"
                    //placeholder="Your Company"
                    data-testid="contact-input-company"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs uppercase tracking-wider text-gray-600 mb-2">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-[#1a1a1a] placeholder-gray-400 focus:outline-none focus:border-[#003151] focus:bg-white transition-colors resize-none"
                  placeholder="Tell us about your requirements..."
                  data-testid="contact-input-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#003151] hover:bg-[#002040] text-white font-semibold px-6 py-4 rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 mt-16 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="font-heading font-bold text-xl text-[#1a1a1a]" data-testid="footer-logo">
              AURUM PRINTS ASSOCIATES
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} AURUM PRINTS ASSOCIATES. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
